import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../../ui/button';
import { RotateCcw } from 'lucide-react';

/**
 * VASScale - Visual Analogue Scale (0-100mm) Component
 * Escala Visual Analógica oficial para medición del dolor
 * 
 * Features:
 * - Línea continua de 100mm conceptual
 * - Click para posicionar el marcador
 * - Drag para ajustar (mouse y touch)
 * - Valor 0-100 (mm)
 */
const VASScale = ({ 
  value, 
  onChange, 
  leftLabel = "Sin dolor", 
  rightLabel = "Peor dolor imaginable",
  showValue = true,
  required = false,
  disabled = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [hasValue, setHasValue] = useState(value !== null && value !== undefined);
  const lineRef = useRef(null);

  // Calcular el valor basado en la posición del click/touch
  const calculateValue = useCallback((clientX) => {
    if (!lineRef.current) return 0;
    
    const rect = lineRef.current.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, relativeX / rect.width));
    const mmValue = Math.round(percentage * 100);
    
    return mmValue;
  }, []);

  // Mouse handlers
  const handleMouseDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(true);
    const newValue = calculateValue(e.clientX);
    onChange(newValue);
    setHasValue(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || disabled) return;
    const newValue = calculateValue(e.clientX);
    onChange(newValue);
  }, [isDragging, disabled, calculateValue, onChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch handlers
  const handleTouchStart = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(true);
    const touch = e.touches[0];
    const newValue = calculateValue(touch.clientX);
    onChange(newValue);
    setHasValue(true);
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || disabled) return;
    const touch = e.touches[0];
    const newValue = calculateValue(touch.clientX);
    onChange(newValue);
  }, [isDragging, disabled, calculateValue, onChange]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Click en la línea
  const handleLineClick = (e) => {
    if (disabled) return;
    const newValue = calculateValue(e.clientX);
    onChange(newValue);
    setHasValue(true);
  };

  // Reiniciar
  const handleReset = () => {
    onChange(null);
    setHasValue(false);
  };

  // Event listeners globales para drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Sincronizar hasValue con prop value
  useEffect(() => {
    setHasValue(value !== null && value !== undefined);
  }, [value]);

  const markerPosition = hasValue && value !== null ? `${value}%` : '0%';

  return (
    <div className="w-full space-y-4">
      {/* Contenedor principal */}
      <div className="relative px-2">
        {/* Labels superiores */}
        <div className="flex justify-between mb-2 text-sm text-gray-600">
          <span className="font-medium">{leftLabel}</span>
          <span className="font-medium">{rightLabel}</span>
        </div>

        {/* Línea VAS */}
        <div 
          ref={lineRef}
          className={`relative h-12 cursor-pointer select-none ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={handleLineClick}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Línea base */}
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-green-200 via-yellow-200 via-orange-200 to-red-300 rounded-full transform -translate-y-1/2 shadow-inner" />
          
          {/* Línea de progreso (opcional, visual) */}
          {hasValue && value !== null && (
            <div 
              className="absolute top-1/2 left-0 h-1.5 bg-gradient-to-r from-green-500 via-yellow-500 via-orange-500 to-red-500 rounded-full transform -translate-y-1/2 transition-all duration-75"
              style={{ width: markerPosition }}
            />
          )}

          {/* Marcadores de escala */}
          <div className="absolute top-1/2 left-0 right-0 flex justify-between transform -translate-y-1/2">
            {[0, 25, 50, 75, 100].map((tick) => (
              <div key={tick} className="relative">
                <div className="w-0.5 h-3 bg-gray-400 rounded" />
              </div>
            ))}
          </div>

          {/* Marcador del paciente */}
          {hasValue && value !== null && (
            <div 
              className={`absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all ${isDragging ? 'scale-110' : ''}`}
              style={{ left: markerPosition }}
            >
              {/* Línea vertical del marcador */}
              <div className="w-1 h-10 bg-blue-600 rounded-full shadow-lg" />
              {/* Círculo superior */}
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full shadow-lg border-2 border-white" />
            </div>
          )}

          {/* Overlay para indicar que no hay valor */}
          {!hasValue && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-sm text-gray-400 bg-white/80 px-3 py-1 rounded">
                Haz clic en la línea para marcar el nivel de dolor
              </span>
            </div>
          )}
        </div>

        {/* Números de referencia */}
        <div className="flex justify-between mt-1 text-xs text-gray-500">
          <span>0</span>
          <span>25</span>
          <span>50</span>
          <span>75</span>
          <span>100</span>
        </div>
      </div>

      {/* Resultado y acciones */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {showValue && hasValue && value !== null && (
            <div className="text-sm">
              <span className="text-gray-500">Resultado: </span>
              <span className="font-bold text-lg text-blue-600">{value}</span>
              <span className="text-gray-500">/100 mm</span>
            </div>
          )}
          
          {hasValue && value !== null && (
            <span className={`text-xs px-2 py-1 rounded-full ${
              value <= 20 ? 'bg-green-100 text-green-700' :
              value <= 40 ? 'bg-lime-100 text-lime-700' :
              value <= 60 ? 'bg-yellow-100 text-yellow-700' :
              value <= 80 ? 'bg-orange-100 text-orange-700' :
              'bg-red-100 text-red-700'
            }`}>
              {value <= 20 ? 'Dolor leve' :
               value <= 40 ? 'Dolor leve-moderado' :
               value <= 60 ? 'Dolor moderado' :
               value <= 80 ? 'Dolor moderado-severo' :
               'Dolor severo'}
            </span>
          )}
        </div>

        {hasValue && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="text-gray-500 hover:text-gray-700"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reiniciar
          </Button>
        )}
      </div>

      {/* Validación */}
      {required && !hasValue && (
        <p className="text-sm text-red-500">* Este campo es obligatorio</p>
      )}
    </div>
  );
};

export default VASScale;
